import React from 'react';
import styled from 'styled-components';
import { useFetchCompany } from '../hooks/useFetchCompany';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(22, 17, 13, 0.42);
  z-index: 400;
  backdrop-filter: blur(3px);
  display: ${({ open }) => (open ? 'block' : 'none')};
`;

const Drawer = styled.div`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 720px;
  background: #fff;
  box-shadow: 0 12px 40px rgba(22, 17, 13, 0.13);
  z-index: 401;
  display: flex;
  flex-direction: column;
  transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.24s cubic-bezier(0.4, 0, 0.2, 1);
`;

const DrawerHeader = styled.div`
  padding: 18px 22px 14px;
  border-bottom: 1px solid #e8ddd6;
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const DrawerTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #16110d;
`;

const DrawerMeta = styled.div`
  font-size: 14px;
  color: #7a6a60;
  margin-top: 3px;
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 18px 22px;
`;

const DrawerFooter = styled.div`
  padding: 14px 22px;
  border-top: 1px solid #e8ddd6;
  flex-shrink: 0;
  display: flex;
  gap: 10px;
`;

const Section = styled.div`
  margin-bottom: 18px;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #b8aaa2;
  text-transform: uppercase;
  letter-spacing: 1.4px;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e8ddd6;
`;

const DR = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 7px 0;
  border-bottom: 1px solid #faf8f6;
`;

const DKey = styled.span`
  font-size: 14px;
  color: #7a6a60;
`;

const DVal = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #16110d;
  text-align: right;
  max-width: 65%;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: 13px;
  font-weight: 600;
  background: ${({ bg }) => bg};
  color: ${({ clr }) => clr};
`;

const PlacePill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #faf8f6;
  border: 1px solid #e8ddd6;
  border-radius: 9px;
  margin-bottom: 7px;
`;

const PlaceDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ active }) => (active ? '#1A7A4A' : '#C42B2B')};
  flex-shrink: 0;
`;

const CloseBtn = styled.button`
  cursor: pointer;
  border: 1px solid #e8ddd6;
  outline: none;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  width: 30px;
  height: 30px;
  padding: 0;
  background: #f2ede8;
  color: #7a6a60;
  &:hover {
    opacity: 0.8;
  }
`;

const EditBtn = styled.button`
  cursor: pointer;
  border: 1px solid #e8ddd6;
  outline: none;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  font-size: 14px;
  font-weight: 600;
  background: #f2ede8;
  color: #7a6a60;
  &:hover {
    opacity: 0.8;
  }
`;

const PrimaryBtn = styled.button`
  flex: 1;
  cursor: pointer;
  border: none;
  outline: none;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 15px;
  font-weight: 600;
  background: #f36e21;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  &:hover {
    opacity: 0.88;
  }
`;

const SecBtn = styled.button`
  cursor: pointer;
  border: 1px solid #e8ddd6;
  outline: none;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 15px;
  font-weight: 600;
  background: #f2ede8;
  color: #7a6a60;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    opacity: 0.88;
  }
`;

const STATUS_TAGS = {
  ACTIVE: { bg: '#E8F5EE', clr: '#1A7A4A', label: 'Active' },
  INACTIVE: { bg: '#FCEAEA', clr: '#C42B2B', label: 'Inactive' },
  PENDING: { bg: '#FEF0E0', clr: '#B06A00', label: 'Pending' },
};

const ABN_TAGS = {
  AVAILABLE: { bg: '#E8F5EE', clr: '#1A7A4A', label: '✓ Verified' },
  UNAVAILABLE: { bg: '#FEF0E0', clr: '#B06A00', label: 'Pending' },
};

const BANK_TAGS = {
  SET: { bg: '#E8F5EE', clr: '#1A7A4A', label: '✓ Bank Set' },
  NOT_SET: { bg: '#FCEAEA', clr: '#C42B2B', label: 'Not configured' },
};

const DataRow = ({ label, value }) => (
  <DR>
    <DKey>{label}</DKey>
    <DVal>{value}</DVal>
  </DR>
);

const getPrimaryEmail = contacts => {
  if (!contacts || contacts.length === 0) return null;
  return (
    contacts.find(c => c.type === 'email' && c.is_primary) ||
    contacts.find(c => c.type === 'email') ||
    null
  );
};

const getPrimaryMobile = contacts => {
  if (!contacts || contacts.length === 0) return null;
  return (
    contacts.find(c => c.type === 'mobile' && c.is_primary) ||
    contacts.find(c => c.type === 'mobile') ||
    null
  );
};

const getPrimaryWebsite = contacts => {
  if (!contacts || contacts.length === 0) return null;
  return (
    contacts.find(c => c.type === 'website' && c.is_primary) ||
    contacts.find(c => c.type === 'website') ||
    null
  );
};

const DetailDrawer = ({
  company,
  onClose,
  onOverlayClose,
  onEdit,
  refreshKey = 0,
}) => {
  const open = !!company;
  const { data: fullData, loading: fullLoading } = useFetchCompany(
    company?.company_id,
    refreshKey,
  );
  const c = fullData ? { ...company, ...fullData } : company;

  return (
    <>
      <Overlay open={open} onClick={onOverlayClose ?? onClose} />
      <Drawer open={open}>
        {c &&
          (() => {
            const company = c;
            const statusTag = STATUS_TAGS[company.status] || STATUS_TAGS.ACTIVE;
            const abnTag = ABN_TAGS[company.abn_status] || ABN_TAGS.UNAVAILABLE;
            const bankTag = BANK_TAGS[company.bank_status] || BANK_TAGS.NOT_SET;
            const address = [
              company.address_line_1,
              company.city,
              company.state,
              company.post_code,
            ]
              .filter(Boolean)
              .join(', ');
            const primaryEmail = getPrimaryEmail(company.contact);
            const primaryMobile = getPrimaryMobile(company.contact);
            const primaryWebsite = getPrimaryWebsite(company.contact);

            const bank = company.bank_detail || {};
            const cfg = company.kravein_configuration || {};
            const addressLine2 = company.address_line_2;

            console.log(company)
            return (
              <>
                <DrawerHeader>
                  <div>
                    <DrawerTitle>{company.name}</DrawerTitle>
                    <DrawerMeta>
                      {[company.city, company.state].filter(Boolean).join(', ')}
                      {company.counts != null
                        ? ` · ${company.counts} place${
                            company.counts !== 1 ? 's' : ''
                          }`
                        : ''}
                    </DrawerMeta>
                  </div>
                  <div
                    style={{ display: 'flex', gap: 7, alignItems: 'center' }}
                  >
                    {onEdit && (
                      <EditBtn
                        onClick={() => {
                          onEdit(company.company_id);
                        }}
                      >
                        ✏ Edit
                      </EditBtn>
                    )}
                    <CloseBtn onClick={onClose}>✕</CloseBtn>
                  </div>
                </DrawerHeader>

                <DrawerBody>
                  {fullLoading && (
                    <div
                      style={{
                        fontSize: 14,
                        color: '#B8AAA2',
                        padding: '6px 0 14px',
                        textAlign: 'center',
                      }}
                    >
                      Loading full details…
                    </div>
                  )}
                  <Section>
                    <SectionTitle>Company Info</SectionTitle>
                    <DataRow
                      label="Company ID"
                      value={
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontSize: 13,
                            color: '#F36E21',
                          }}
                        >
                          {company.company_id}
                        </span>
                      }
                    />
                    <DataRow label="Name" value={company.name} />
                    <DataRow
                      label="Street Address"
                      value={company.address_line_1 || '—'}
                    />
                    {addressLine2 && (
                      <DataRow label="Address Line 2" value={addressLine2} />
                    )}
                    <DataRow
                      label="Suburb / City"
                      value={
                        [company.city, company.state, company.post_code]
                          .filter(Boolean)
                          .join(', ') || '—'
                      }
                    />
                    <DataRow
                      label="Status"
                      value={
                        <Tag bg={statusTag.bg} clr={statusTag.clr}>
                          {statusTag.label}
                        </Tag>
                      }
                    />
                  </Section>

                  <Section>
                    <SectionTitle>Tax</SectionTitle>
                    <DataRow
                      label="ABN"
                      value={
                        <span style={{ fontFamily: 'monospace' }}>
                          {company.registration_id || company.abn || '—'}
                        </span>
                      }
                    />
                    <DataRow
                      label="ABN Status"
                      value={
                        <Tag bg={abnTag.bg} clr={abnTag.clr}>
                          {abnTag.label}
                        </Tag>
                      }
                    />
                  </Section>

                  <Section>
                    <SectionTitle>Contact Details</SectionTitle>
                    <DataRow
                      label="Email"
                      value={
                        primaryEmail ? (
                          <a
                            href={`mailto:${primaryEmail.value}`}
                            style={{ color: '#F36E21' }}
                          >
                            {primaryEmail.value}
                          </a>
                        ) : (
                          '—'
                        )
                      }
                    />
                    <DataRow
                      label="Mobile"
                      value={
                        primaryMobile ? (
                          <a
                            href={`tel:${primaryMobile.value}`}
                            style={{ color: '#F36E21' }}
                          >
                            {primaryMobile.value}
                          </a>
                        ) : (
                          '—'
                        )
                      }
                    />
                    <DataRow
                      label="Website"
                      value={
                        primaryWebsite?.value ? (
                          <a
                            href={primaryWebsite.value}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#F36E21' }}
                          >
                            {primaryWebsite.value}
                          </a>
                        ) : (
                          '—'
                        )
                      }
                    />
                  </Section>

                  <Section>
                    <SectionTitle>Bank / Settlement</SectionTitle>
                    <DataRow
                      label="Status"
                      value={
                        <Tag bg={bankTag.bg} clr={bankTag.clr}>
                          {bankTag.label}
                        </Tag>
                      }
                    />
                    <DataRow
                      label="Account Name"
                      value={bank.account_name || '—'}
                    />
                    <DataRow
                      label="Account Number"
                      value={bank.account_number || '—'}
                    />
                    <DataRow label="Bank Name" value={bank.bank_name || '—'} />
                    <DataRow
                      label="Branch Name"
                      value={bank.branch_name || '—'}
                    />
                    <DataRow label="IFSC Code" value={bank.ifsc_code || '—'} />
                    <DataRow
                      label="Payment Method"
                      value={bank.payment_method || '—'}
                    />
                  </Section>

                  <Section>
                    <SectionTitle>KRAVEiN Invoice Configuration</SectionTitle>
                    <DataRow
                      label="Invoice Cycle"
                      value={cfg.invoice_cycle || '—'}
                    />
                    <DataRow
                      label="Auto-send Invoice"
                      value={
                        cfg.auto_send_invoice == null
                          ? '—'
                          : cfg.auto_send_invoice
                          ? 'Yes — auto-send on generation'
                          : 'No — manual approval required'
                      }
                    />
                    <DataRow
                      label="Invoice Email"
                      value={cfg.invoice_email || '—'}
                    />
                  </Section>

                  {company.places && company.places.length > 0 && (
                    <Section>
                      <SectionTitle>
                        Linked Places ({company.counts ?? company.places.length}
                        )
                      </SectionTitle>
                      {company.places.slice(0, 5).map(place => (
                        <PlacePill key={place.place_id}>
                          <PlaceDot active={place.status === 'ACTIVE'} />
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div
                              style={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: '#16110D',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {place.name}
                            </div>
                            <div style={{ fontSize: 13, color: '#B8AAA2' }}>
                              {[place.address_line_1, place.city, place.state]
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                          </div>
                          <Tag
                            bg={
                              place.status === 'ACTIVE' ? '#E8F5EE' : '#FCEAEA'
                            }
                            clr={
                              place.status === 'ACTIVE' ? '#1A7A4A' : '#C42B2B'
                            }
                          >
                            {place.status}
                          </Tag>
                        </PlacePill>
                      ))}
                      {company.counts > 5 && (
                        <div
                          style={{
                            textAlign: 'center',
                            fontSize: 14,
                            color: '#B8AAA2',
                            padding: '8px 0',
                          }}
                        >
                          +{company.counts - 5} more places
                        </div>
                      )}
                    </Section>
                  )}
                </DrawerBody>

                <DrawerFooter>
                  <SecBtn onClick={onClose}>Close</SecBtn>
                </DrawerFooter>
              </>
            );
          })()}
      </Drawer>
    </>
  );
};

export default DetailDrawer;
